import { useLang } from "@/contexts/LangContext";
import SectionReveal from "@/components/SectionReveal";
import {
  Smartphone,
  MessageSquare,
  Map,
  CreditCard,
  Wifi,
  Plug,
  Languages,
  Car,
  ShoppingBag,
  Globe,
  Download,
  CheckCircle2,
  AlertTriangle,
  PlayCircle,
  Headphones,
  ShieldCheck,
  Sparkles,
  ArrowRight,
} from "lucide-react";

const WHATSAPP_NUMBER = "971589656886";
const WHATSAPP_DISPLAY = "+971 58 965 6886";

const ESSENTIAL_APPS = [
  {
    icon: MessageSquare,
    name: "WeChat",
    sub: "微信",
    body: "China's super-app — messaging, payments, mini-programs, hotel check-in, food delivery. Essential before you fly.",
    tag: "Must-have",
  },
  {
    icon: CreditCard,
    name: "Alipay",
    sub: "支付宝",
    body: "Now accepts foreign Visa / Mastercard / Amex cards. Pay everywhere by QR — taxis, street food, museums.",
    tag: "Must-have",
  },
  {
    icon: Map,
    name: "Amap (Gaode)",
    sub: "高德地图",
    body: "Google Maps doesn't work in China. Amap has English mode, public transit, walking and driving navigation.",
    tag: "Maps",
  },
  {
    icon: Car,
    name: "DiDi",
    sub: "滴滴出行",
    body: "China's Uber. Add a payment method in advance. English interface available. Often cheaper than airport taxis.",
    tag: "Transport",
  },
  {
    icon: Languages,
    name: "Pleco / Baidu Translate",
    sub: "翻译",
    body: "Pleco for Chinese↔English dictionary; Baidu / Google Translate (offline) for menus, signs and photos.",
    tag: "Language",
  },
  {
    icon: ShoppingBag,
    name: "Book With Us",
    sub: "The China Explorer",
    body: "Skip the third-party OTAs — our concierge team books your flights, trains, hotels and transfers directly at partner rates, with bilingual support and on-the-ground backup.",
    tag: "Concierge",
  },
];

const PRACTICAL_TIPS = [
  {
    icon: Wifi,
    title: "Connectivity",
    body: "Buy an eSIM (Airalo, Holafly) before flying — works on arrival, no VPN headaches. Local SIMs are cheap but require passport registration.",
  },
  {
    icon: Globe,
    title: "VPN (Optional)",
    body: "Want WhatsApp / Instagram / Google / Gmail? Install a reputable VPN (ExpressVPN, Astrill) BEFORE you arrive — installation is harder once inside China.",
  },
  {
    icon: Plug,
    title: "Power & Adapters",
    body: "China uses 220V / 50Hz with Type A (US flat), Type C (EU round) and Type I (AU angled) sockets. A universal adapter covers everything.",
  },
  {
    icon: CreditCard,
    title: "Cash & Cards",
    body: "Bring some RMB cash (¥500–1000) for small markets and tipping. Most foreign Visa/Mastercard/Amex cards now work via Alipay/WeChat Pay.",
  },
  {
    icon: AlertTriangle,
    title: "On Arrival",
    body: "Fill the Customs Health Declaration via the official customs WeChat mini-program (海关旅客指尖服务) within 24h before landing — saves time at immigration.",
  },
  {
    icon: Download,
    title: "Pre-Download List",
    body: "WeChat · Alipay · Amap · DiDi · Pleco · Airalo · Customs Declaration mini-app · your VPN of choice. Do this BEFORE boarding.",
  },
];

const ETIQUETTE = {
  dos: [
    "Greet with a warm smile — handshakes are common, hugs are not",
    "Receive business cards with both hands and look at them",
    "Try chopsticks — locals appreciate the effort",
    "Carry your passport everywhere — required for trains and hotels",
    "Bargain in markets, never in malls or chain stores",
  ],
  donts: [
    "Don't stick chopsticks upright in rice (funeral symbolism)",
    "Don't discuss politics with strangers",
    "Don't expect tipping — it's generally not customary",
    "Don't post Tibet/Taiwan maps without context on Chinese social",
    "Don't drink tap water — buy bottled or use the hotel kettle",
  ],
};

const TravelGuide = () => {
  const { t } = useLang();

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="guide"
      data-testid="guide-section"
      className="bg-[#EAE6DF] py-24 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <SectionReveal className="max-w-3xl">
          <p className="text-xs md:text-sm uppercase tracking-[0.3em] text-[#2E5D4B] font-semibold mb-4">
            ✦ Travel to China · Pre-Flight Guide
          </p>
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl tracking-tight text-[#121212] leading-[1.05]">
            What to do <em className="font-heading italic">before</em> you board.
          </h2>
          <p className="mt-6 text-lg text-[#4A4A4A] leading-relaxed">
            China runs on its own digital ecosystem. Land prepared with the right apps,
            payment setup and a few cultural shortcuts — and your trip will feel half-local
            from day one.
          </p>
        </SectionReveal>

        {/* Essential apps */}
        <SectionReveal delay={0.1} className="mt-14">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-[#C62828] font-semibold mb-2">
                Step 1 · Download these
              </p>
              <h3 className="font-heading text-2xl md:text-3xl text-[#121212]">
                Six apps to install before you fly
              </h3>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {ESSENTIAL_APPS.map((a, i) => {
              const Icon = a.icon;
              return (
                <div
                  key={a.name}
                  data-testid={`guide-app-${i}`}
                  className="group bg-white border border-black/5 p-6 hover:border-[#C62828]/30 hover:-translate-y-0.5 transition-all duration-500"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="w-12 h-12 flex items-center justify-center bg-[#C62828]/10 text-[#C62828] group-hover:bg-[#C62828] group-hover:text-white transition-colors duration-500">
                      <Icon size={22} strokeWidth={1.5} />
                    </div>
                    <span className="text-[10px] uppercase tracking-wider font-bold text-[#D4AF37] mt-1">
                      {a.tag}
                    </span>
                  </div>
                  <h4 className="mt-5 font-heading text-2xl text-[#121212]">{a.name}</h4>
                  <p className="text-xs uppercase tracking-wider text-[#4A4A4A] mt-0.5">
                    {a.sub}
                  </p>
                  <p className="mt-3 text-sm text-[#4A4A4A] leading-relaxed">{a.body}</p>
                </div>
              );
            })}
          </div>
        </SectionReveal>

        {/* Practical tips */}
        <SectionReveal delay={0.15} className="mt-20">
          <p className="text-xs uppercase tracking-[0.3em] text-[#C62828] font-semibold mb-2">
            Step 2 · Set up
          </p>
          <h3 className="font-heading text-2xl md:text-3xl text-[#121212]">
            Connectivity, money, power & arrival paperwork
          </h3>

          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {PRACTICAL_TIPS.map((tip, i) => {
              const Icon = tip.icon;
              return (
                <div
                  key={tip.title}
                  data-testid={`guide-tip-${i}`}
                  className="bg-[#F7F5F0] border-l-4 border-[#0E2A5C] p-6"
                >
                  <div className="flex items-center gap-3">
                    <Icon size={18} className="text-[#0E2A5C]" />
                    <h4 className="font-heading text-xl text-[#121212]">{tip.title}</h4>
                  </div>
                  <p className="mt-3 text-sm text-[#4A4A4A] leading-relaxed">{tip.body}</p>
                </div>
              );
            })}
          </div>
        </SectionReveal>

        {/* Etiquette do's and don'ts */}
        <SectionReveal delay={0.2} className="mt-20">
          <p className="text-xs uppercase tracking-[0.3em] text-[#C62828] font-semibold mb-2">
            Step 3 · Blend in
          </p>
          <h3 className="font-heading text-2xl md:text-3xl text-[#121212]">
            Cultural do's & don'ts
          </h3>

          <div className="mt-8 grid md:grid-cols-2 gap-6">
            <div
              data-testid="guide-dos"
              className="bg-[#2E5D4B] text-white p-7 md:p-9"
            >
              <p className="text-xs uppercase tracking-[0.3em] text-[#3BFFC2] font-semibold mb-4">
                ✓ Do
              </p>
              <ul className="space-y-3">
                {ETIQUETTE.dos.map((d, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-white/90 leading-relaxed">
                    <CheckCircle2 size={16} className="text-[#3BFFC2] mt-0.5 flex-shrink-0" />
                    {d}
                  </li>
                ))}
              </ul>
            </div>
            <div
              data-testid="guide-donts"
              className="bg-[#0A0A0A] text-white p-7 md:p-9"
            >
              <p className="text-xs uppercase tracking-[0.3em] text-[#FFB4B4] font-semibold mb-4">
                ✗ Don't
              </p>
              <ul className="space-y-3">
                {ETIQUETTE.donts.map((d, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-white/90 leading-relaxed">
                    <AlertTriangle size={16} className="text-[#FFB4B4] mt-0.5 flex-shrink-0" />
                    {d}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </SectionReveal>

        {/* === Your Personal Travel Guide — book with us directly === */}
        <SectionReveal delay={0.18} className="mt-20">
          <div className="relative overflow-hidden bg-gradient-to-br from-[#1F3D2E] via-[#2E5D4B] to-[#16291F] text-white p-8 md:p-14">
            <div className="absolute -top-24 -right-24 w-72 h-72 bg-[#D4AF37]/25 rounded-full blur-3xl" />
            <div className="absolute -bottom-20 -left-20 w-72 h-72 bg-black/30 rounded-full blur-3xl" />
            <div className="relative grid lg:grid-cols-12 gap-10 items-start">
              <div className="lg:col-span-6">
                <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] font-semibold mb-3 inline-flex items-center gap-2">
                  <Sparkles size={14} /> Your Travel Guide
                </p>
                <h3 className="font-heading text-3xl md:text-5xl leading-tight">
                  Skip the OTAs.<br />
                  <em className="font-heading italic">Book directly with us.</em>
                </h3>
                <p className="mt-5 text-white/85 leading-relaxed max-w-xl">
                  The China Explorer is not a faceless platform. Every trip is hand-built by
                  our Dubai-based concierge team and supported on the ground in China by
                  bilingual local guides — at better-than-OTA rates, with one human you can
                  reach 24/7.
                </p>

                <div className="mt-7 grid sm:grid-cols-2 gap-3">
                  <a
                    data-testid="guide-whatsapp-cta"
                    href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
                      "Hi The China Explorer! I'd like to book my trip directly with your team. Please share what you can offer."
                    )}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1da851] text-white px-6 py-3.5 uppercase tracking-wider text-sm font-bold transition-colors"
                  >
                    <MessageSquare size={16} /> Chat on WhatsApp
                    <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                  </a>
                  <button
                    data-testid="guide-plan-cta"
                    onClick={() => scrollTo("itinerary")}
                    className="inline-flex items-center justify-center gap-2 bg-white text-[#2E5D4B] hover:bg-[#F7F5F0] px-6 py-3.5 uppercase tracking-wider text-sm font-bold transition-colors"
                  >
                    <Sparkles size={16} /> Plan with Mei AI
                  </button>
                </div>

                <p className="mt-5 text-xs text-white/65">
                  Direct line: <span className="text-white font-semibold">{WHATSAPP_DISPLAY}</span> · Dubai, UAE
                </p>
              </div>

              <div className="lg:col-span-6">
                <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] font-semibold mb-5">
                  What you get when you book direct
                </p>
                <ul className="space-y-4">
                  {[
                    {
                      Icon: Headphones,
                      title: "A real human concierge",
                      body: "One curator from inquiry to homecoming — no chatbots, no call centres.",
                    },
                    {
                      Icon: ShieldCheck,
                      title: "Better-than-OTA pricing",
                      body: "Direct supplier contracts with hotels, drivers and rail operators.",
                    },
                    {
                      Icon: Globe,
                      title: "On-the-ground backup",
                      body: "Bilingual guides in 12 Chinese cities, available 24/7 via WhatsApp.",
                    },
                    {
                      Icon: CheckCircle2,
                      title: "Flex-friendly bookings",
                      body: "Refundable deposit, free changes up to 14 days before departure.",
                    },
                  ].map(({ Icon, title, body }) => (
                    <li
                      key={title}
                      className="flex items-start gap-4 bg-white/[0.08] backdrop-blur-sm border border-white/15 p-4"
                    >
                      <span className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center flex-shrink-0">
                        <Icon size={17} className="text-[#D4AF37]" />
                      </span>
                      <span>
                        <p className="font-heading text-lg leading-tight">{title}</p>
                        <p className="text-sm text-white/75 mt-1 leading-relaxed">{body}</p>
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </SectionReveal>

        {/* Video placeholder */}
        <SectionReveal delay={0.25} className="mt-20">
          <div
            data-testid="guide-video-card"
            className="relative overflow-hidden bg-[#0E2A5C] text-white p-10 md:p-16 grid lg:grid-cols-12 gap-10 items-center"
          >
            <div className="absolute -top-20 -right-20 w-80 h-80 bg-[#C62828]/20 rounded-full blur-3xl" />
            <div className="relative lg:col-span-7">
              <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] font-semibold mb-3">
                Coming Soon
              </p>
              <h3 className="font-heading text-3xl md:text-4xl leading-tight">
                "Foreigners in China": a 60-second prep guide
              </h3>
              <p className="mt-5 text-white/75 leading-relaxed max-w-xl">
                We're filming a short video featuring real travelers from the UAE, Europe and
                Southeast Asia sharing exactly how they set up WeChat, Alipay and their phone
                before flying. Stay tuned.
              </p>
              <button
                data-testid="guide-video-cta"
                onClick={() => scrollTo("contact")}
                className="mt-6 inline-flex items-center gap-2 border border-white/30 hover:border-[#D4AF37] hover:text-[#D4AF37] text-white px-6 py-3 uppercase tracking-wider text-xs font-semibold transition-colors"
              >
                <PlayCircle size={16} /> Notify me when it's live
              </button>
            </div>
            <div className="relative lg:col-span-5">
              <div className="aspect-video bg-black/40 border border-white/10 flex items-center justify-center group cursor-not-allowed">
                <div className="text-center">
                  <PlayCircle size={56} className="mx-auto text-white/50" strokeWidth={1.3} />
                  <p className="mt-3 text-xs uppercase tracking-[0.3em] text-white/40">
                    Video coming soon
                  </p>
                </div>
              </div>
            </div>
          </div>
        </SectionReveal>
      </div>
    </section>
  );
};

export default TravelGuide;
